import _ from 'lodash';
import Data from 'app-core/Data';
import linkUnlink from 'view/audioTracker/linkUnlink';
import ssmlGenerator from './ssml-generator';
import storage from 'storage';
import config from 'app-core/config';
import videoHelper from 'view/audioTracker/video-helper';
import axios from 'axios';
import ssmlHelper from 'view/insertion/ssml-helper';
import audio from 'app-core/audio';
import audioHelper from 'view/audioTracker/audio-helper';

function getTypeFromInsertion(r) {
	let type = r.type || r.Type || '';
	if (type === 'textToSpeech' || type.indexOf('SsmlInsertion') > -1)
		return 'textToSpeech';
	if (type === 'audioFile' || type.indexOf('AudioInsertion') > -1)
		return 'audioFile';
	if (type === 'break' || type.indexOf('BreakInsertion') > -1)
		return 'break';
	if (type === 'video' || type.indexOf('VideoInsertion') > -1)
		return 'video';
}

export {
	getTypeFromInsertion
}

export default {
	computed: {
		downloadUrlIsVimeoOrYoutube() {
			return this.fileUploading.downloadUrl && ['youtube.com', 'youtube-nocookie.com', 'youtu.be', 'vimeo'].some(f => `${this.fileUploading.downloadUrl}`.toLowerCase().indexOf(f) > -1);
		},
		isMediaUploadingSourceSelected() {
			return (this.fileUploading.selected || this.fileUploading.downloadUrl);
		},
		isMediaUploadingSourceVideo() {
			return this.downloadUrlIsVimeoOrYoutube || this.fileUploading.isVideo;
		},
		isMediaUploadingTranslateAvailable() {
			return this.isMediaUploadingSourceSelected && (!this.isMediaUploadingSourceVideo || (this.fileUploading.selected || (this.isMediaUploadingSourceVideo && this.fileUploading.grabAudio)));
		},
		isExtractingVoiceOrInstrumentals() {
			return this.isMediaUploadingSourceSelected && (this.fileUploading.extractVoice || this.fileUploading.extractInstrumentals);
		}
	},
	data() {
		return {
			linkingInsertion: null,
			linkingSourceType: 'both',
			editingTrack: null,
			editingParentInsertion: null,
			fileUploading: {
				trim: false,
				trimDbLevel: 0,
				normalize: false,
				normalizeDbLevel: 0,
				selected: false,
				isVideo: null,
				downloadUrl: null,
				grabAudio: true,
				grabVideo: true,
				translate: false,
				transcribeOnly: true,
				sourceLanguage: null,
				targetLanguage: null,
				extractVoice: false,
				extractInstrumentals: false,
				denoiser: false
			},
			textToSpeech: {
				splitVisible: false
			},
			hasVoiceMessages: false
		}
	},
	mounted() {
		this.checkHasVoiceMessages();

		this.$eventsBus.$on('creative--add-media-from-media-file', this.addMediaFromMediaFile);
	},
	destroyed() {
		this.$eventsBus.$off('creative--add-media-from-media-file', this.addMediaFromMediaFile);
	},
	watch: {
		'fileUploading.sourceLanguage'(v) {
			if (this.fileUploading.transcribeOnly) {
				this.fileUploading.targetLanguage = v;
			}
		}
	},
	methods: {
		async addMediaFromMediaFile(opts) {
			let mediaFile = opts?.mediaFile;
			let text = opts?.text || this.$tCaption('New insertion');

			if (!mediaFile?.Oid) return;

			if (__DEBUG__)
				console.debug('addMediaFromMediaFile', { opts });

			this.isLoading++;
			try {
				let settings = await this.$Data.getAzureSettings();

				let newMedia = await this.$Data.postExternalApi('azurefile/copyTo', {
					Oid: mediaFile?.Oid,
					CopyToContainer: settings?.BaseAudioContainerName || 'baseaudio'
				});

				if (!newMedia?.Oid) return;

				let audioInsertion = {
					CreativeId: this.creative.Oid,
					Name: text,
					type: 'audioFile',
					JsonConfig: {
						trackNumber: this.$refs.tracker?.focusedAggregatedTrack?.trackNumber
					},
					Audio: {
						Oid: newMedia?.Oid
					},
					StartTime: Math.round(this.$refs.tracker.currentTime * 1000),
				};
				
				const insertion = (await this.$Data.createUpdatePureInsertion(audioInsertion))?.Data;
				insertion.Audio = newMedia;

				let track = this.getTrackFromInsertion(insertion);
				this.processAddTrack(track);
				this.calcVisibleTracks();
			} finally {
				this.isLoading--;
			}
		},
		async checkHasVoiceMessages() {
			this.hasVoiceMessages = (await this.$Data.getExternalApi('voicemessage/list', '', 1))?.TotalNumberOfRecords > 0;
		},
		getTypeFromInsertion(r) {
			return getTypeFromInsertion(r);
		},
		checkSsmlTrack(track) {
			if (track.__isInsertionLoaded || this.inMemory) return Promise.resolve(track);
			if (this.isPublishMode) {
				track.__isInsertionLoaded = true;
				return Promise.resolve(track);
			}
			const processInsertion = (r) => {
				if (this._isDestroyed) return;
				let insertion = this.getTrackFromInsertion(r).insertion;
				delete insertion.JsonConfig;
				_.merge(track.insertion, _.omit(insertion, ['StartTime', 'StartOffset', 'EndOffset', 'Duration']));
				track.__isInsertionLoaded = true;
				track.__tracker.updateTracksList();
			}
			if (track.type === 'textToSpeech') {
				if (!track?.insertion?.Oid) return Promise.resolve(null);
				this.isTracksLoading++;
				return Data
					.getExternalApi('creative/GetSsmlInsertion', `insertionId=${track.id}`)
					.then(r => processInsertion(r))
					.finally(() => this.isTracksLoading--);
			} else if (track.type === 'audioFile') {
				if (!track?.insertion?.Oid) return Promise.resolve(null);
				this.isTracksLoading++;
				return Data
					.getExternalApi('creative/GetAudioInsertion', `insertionId=${track.id}`)
					.then(r => processInsertion(r))
					.finally(() => this.isTracksLoading--);
			} else if (track.type === 'break') {
				this.isTracksLoading++;
				return Data
					.getExternalApi('creative/GetBreakInsertion', `insertionId=${track.id}`)
					.then(r => processInsertion(r))
					.finally(() => this.isTracksLoading--);
			} else {
				track.__isInsertionLoaded = true;
				return Promise.resolve(track)
			}
		},
		createInsertion(kind, parentInsertion) {
			if (__DEBUG__)
				console.info('Create insertion', { kind, parentInsertion });

			this.editingTrack = null;
			this.editingParentInsertion = null;

			switch (kind) {
				case 'media':
					this.fileUploading.downloadUrl = null;
					this.fileUploading.trimDbLevel = this.fileUploading.trimDbLevel || storage.prop('audio-tracker-trim-silence-db-level', void 0, 0.1).value || 0.1;
					this.fileUploading.normalizeDbLevel = this.fileUploading.normalizeDbLevel || storage.prop('audio-tracker-normalize-db-level', void 0, -3).value || -3;
					this.fileUploading.selected = false;
					this.fileUploading.grabAudio = true;
					this.fileUploading.grabVideo = true;
					this.fileUploading.translate = false;
					this.fileUploading.transcribeOnly = true;
					this.fileUploading.extractInstrumentals = false;
					this.fileUploading.extractVoice = false;
					this.fileUploading.denoiser = false;
					if (!this.fileUploading.sourceLanguage) this.fileUploading.sourceLanguage = this.languages.find(f => f.locale === this.creative.Language?.Code);
					if (!this.fileUploading.targetLanguage) this.fileUploading.targetLanguage = this.languages.find(f => f.locale === this.creative.Language?.Code);
					this.editingParentInsertion = parentInsertion;
					this.$refs.insertionMediaUploader.showPopup();
					break;
				case 'audioFile':
					this.fileUploading.trimDbLevel = this.fileUploading.trimDbLevel || storage.prop('audio-tracker-trim-silence-db-level', void 0, 0.1).value || 0.1;
					this.fileUploading.normalizeDbLevel = this.fileUploading.normalizeDbLevel || storage.prop('audio-tracker-normalize-db-level', void 0, -3).value || -3;
					this.fileUploading.selected = false;
					this.$refs.insertionAudioUploader.showPopup();
					this.editingParentInsertion = parentInsertion;
					break;
				case 'video':
					this.fileUploading.trimDbLevel = null;
					this.fileUploading.normalizeDbLevel = null;
					this.fileUploading.selected = false;
					this.$refs.insertionVideoUploader.showPopup();
					this.editingParentInsertion = parentInsertion;
					break;
				case 'record':
					this.$refs.tracker.recordVoice();
					break;
				case 'voiceMessage':
					this.$refs.voiceMessage.show();
					break;
				case 'break': {
					const breakInsertion = {
						Name: 'Break',
						StartTime: Math.round(this.$refs.tracker.currentTime * 1000),
						Duration: 0,
						CreativeId: this.creativeId,
						BreakType: 0,
						JsonConfig: {
							trackNumber: this.$refs.tracker?.focusedAggregatedTrack?.trackNumber
						},
						type: 'break'
					};
					this.editingTrack = this.getTrackFromInsertion(breakInsertion);
					this.breakPopupVisible = false;
					this.$nextTick(() => this.breakPopupVisible = true);
					break;
				}
				case 'textToSpeech':
					if (parentInsertion) {
						let subInsertion = {
							..._.clone(parentInsertion),
							Name: null,
							Oid: null,
							ParentId: parentInsertion.Oid,
							SubInsertions: [],
							EventRule: null,
							isNew: true,
							audio: null,
							buffer: null,
							openDialog: true,
							type: 'textToSpeech',
							JsonConfig: {
								trackNumber: this.$refs.tracker?.focusedAggregatedTrack?.trackNumber
							}
						};

						delete subInsertion.CountOfSubInsertions;
						delete subInsertion.Oid;
						delete subInsertion.Type;
						if (__DEBUG__)
							console.debug('New sub insertion', {
								subInsertion,
								id: subInsertion.id,
								open: subInsertion.openDialog
							});
						parentInsertion.SubInsertions.push(subInsertion);
								
						this.editingTrack = this.getTrackFromInsertion(subInsertion, void 0, { __isSubTrack: true });
					} else {
						let insertion = {
							Name: '',
							Text: '',
							Ssml: '',
							StartTime: parseInt(this.$refs.tracker.currentTime * 1000),
							StartOffset: 0,
							EndOffset: 0,
							Duration: 2000,
							Language: storage.prop('ssmlLanguage').value || this.$auth.getBrowserLocale().replace('-', ''),
							Voice: this.focusedAggregatedTrackSsmlObject()?.insertion?.Voice,
							VoiceStyle: null,
							Rate: 1,
							Pitch: 1,
							CreativeId: this.creativeId,
							SubInsertions: [],
							audio: null,
							openDialog: true,
							isPlaying: false,
							isNew: true,
							type: 'textToSpeech',
							JsonConfig: {
								trackNumber: this.$refs.tracker?.focusedAggregatedTrack?.trackNumber
							},
							TtsProviderType: 0,
							VoiceSettings: null
						};

						this.editingTrack = this.getTrackFromInsertion(insertion);
						this.editingTrack.__preventLinksProcessing = true;
					}

					this.$refs.insertionEditor.showInsertion();
					break;
				case 'aiScript':
					this.$router.push({
						name: 'script-builder',
						params: {
							attachToCreative: {
								creativeId: this.creative.Oid,
								startTime: this.$refs.tracker.currentTime,
								trackNumber: this.$refs.tracker?.focusedAggregatedTrack?.trackNumber
							}
						}
					});
					break;
			}
		},
		uploadAudio(insertion, base64) {
			if (this.inMemory) return;
			if (__DEBUG__)
				console.debug('Upload creative audio', { base64 });
			this.isLoading++;
			this.$azure.uploadInsertionAudio(insertion, base64)
				.then(() => {
					let track = this.getTrackFromInsertion(insertion);
					track.src = base64;
					track.__isSrcLoaded = false;
					track.__isSubTrack = !!this.editingParentInsertion;

					this.processAddTrack(track);
					this.$refs.tracker.setBase64ForTrack(track, base64);
					this.calcVisibleTracks();
				})
				.catch((ex) => {
					this.$notifyError(ex);
				})
				.finally(() => this.isLoading--);
		},
		async onNewInsertionFileUpload(v, opts) {
			if (__DEBUG__)
				console.debug('onNewInsertionFileUpload', { v, opts });

			if (this.fileUploading.trim) {
				storage.prop('audio-tracker-trim-silence-db-level', void 0, 0.1).value = this.fileUploading.trimDbLevel;
			}

			if (this.fileUploading.normalize) {
				storage.prop('audio-tracker-normalize-db-level', void 0, -3).value = this.fileUploading.normalizeDbLevel;
			}

			const currentAggTracks = [...this.creative.JsonConfig.aggregatedTracks];
			const processExtracting = async () => {
				this.isLoading++;
				try {
					const azureAudios = await this.extractVoiceAndInstrumentals({ blob: v[0] });

					if (azureAudios?.length) {
						await Promise.all((azureAudios || []).map(async (f, idx) => {
							const aggTrack = this.createNewAggregatedTrack();
							let name = f?.FileName?.indexOf('_vocals') === -1 ? '' : (f?.FileName?.indexOf('no_vocals') > -1 ? this.$tCaption('Instrumentals') : this.$tCaption('Vocals'))
							return this.createAudioInsertionFromAzureFile({
								insertionInfo: {
									StartTime: opts?.startTime || 0,
									Duration: 0,
									JsonConfig: {
										trackNumber: aggTrack?.trackNumber
									},
									Name: this.getNumberedInsertionName(name || this.$tCaption('Extracted audio'), !name ? idx : 0)
								},
								azureInfo: f,
								opts
							})
						}));
					}
				} finally {
					this.isLoading--;
				}
			}

			if (v?.length) {
				if (opts?.translate) {
					await this.createSsmlInsertionFromAudio(v && v[0]?.name, v && v[0], opts);
					if (opts?.isExtractingVoiceOrInstrumentals) {
						await processExtracting();
					}
				} else {
					if (opts?.isExtractingVoiceOrInstrumentals) {
						await processExtracting();
					} else {
						const base64 = await this.$Data.blob2Base64(v[0]);
						await this.onNewBase64FileData(base64, v[0].name, void 0, opts);
					}
				}
			}

			if (this.editingTrack && this.$refs.audioInsertionDetails) {
				this.$refs.audioInsertionDetails.assignValues();
				this.editingTrack.__saveInsertion('onNewInsertionFileUpload');
			}

			this.removeEmptyAggregatedTracks(currentAggTracks);
		},
		getAggTrackByType(type, opts, useEmpty) {
			if (!type) return null;
			let aggTrack = this.creative?.JsonConfig?.aggregatedTracks?.find(f => f.__type == type);
			if (!aggTrack) {
				if (useEmpty && !this.creative?.JsonConfig?.aggregatedTracks.some(f => f?.__tracks?.length)) {
					aggTrack = this.creative?.JsonConfig?.aggregatedTracks.find(f => !f?.__tracks?.length && !f?.__type);
				}
				if (!aggTrack) {
					aggTrack = this.createNewAggregatedTrack();
				}
				this.$set(aggTrack, '__type', type);

				if (opts) {
					_.assign(aggTrack, opts);
				}
			}
			return aggTrack;
		},
		async createVideoInsertionFromAzureFile(fileName, azureInfo, insertionInfo, opts) {
			const videoAggTrack = this.getAggTrackByType('video', {
				name: this.$tCaption('Video')
			});
			let videoInsertion = {
				CreativeId: this.creative.Oid,
				Name: fileName || azureInfo.FileName,
				type: 'video',
				JsonConfig: {
					trackNumber: videoAggTrack?.trackNumber
				},
				...(insertionInfo || {})
			};
			
			const insertion = (await this.$Data.createUpdatePureInsertion(videoInsertion))?.Data;
			insertion.Audio = azureInfo;
			if (__DEBUG__)
				console.debug('VIDEO 0', { insertion, videoInsertion });
			let track = this.getTrackFromInsertion(insertion);
			this.processAddTrack(track);
			this.calcVisibleTracks();
			_.assign(videoInsertion, insertion);
			if (__DEBUG__)
				console.debug('VIDEO 1', { videoInsertion, insertion });
			if (videoInsertion.Oid) {
				const payload = azureInfo;
				payload.ObjectId = videoInsertion.Oid;
				if (__DEBUG__)
					console.debug('VIDEO 4', { payload });
				await Data.postExternalApi('Creative/SaveAzureAudioToInsertion', payload);
			}

			return track;
		},
		async createVideoAudioInsertionFromAzureFile(fileName, azureInfo, insertionInfo, aggTrackInfo, opts) {
			if (opts?.denoiser) {
				this.isLoading++;
				const denoisedBlob = await this.$Data.denoiseMedia({ azureFile: azureInfo, asBlob: true, fileName: fileName || azureInfo.FileName }).finally(() => this.isLoading--);
				azureInfo = await this.$azure.uploadBlobWithInfoResponse(`${this.$Data.uuid()}_denoised_${fileName || azureInfo.FileName}`, denoisedBlob);

				if (__DEBUG__)
					console.debug('createVideoInsertionFromAzureFile denoising', { denoisedBlob, azureInfo });
			}

			return this.createAudioInsertionInTypedTrack({
				fileName,
				azureInfo,
				insertionInfo
			}, this.getAggTrackByType('video-audio', {
				name: this.$tCaption('Video: audio track'),
				...(aggTrackInfo || {})
			}));
		},
		async createAudioInsertionFromAzureFile({ fileName, azureInfo, insertionInfo, blob, opts }) {
			if (__DEBUG__)
				console.debug('createAudioInsertionFromAzureFile', { fileName, azureInfo, insertionInfo, blob, opts });

			let audioInsertion = {
				CreativeId: this.creative.Oid,
				Name: fileName || azureInfo.FileName,
				type: 'audioFile',
				JsonConfig: {
					trackNumber: 0
				},
				...(insertionInfo || {})
			};

			if (!azureInfo) {
				azureInfo = await this.$azure.uploadBlobWithInfoResponse(`${this.$Data.uuid()}_${fileName}`, blob);
			}

			if (opts?.denoiser) {
				this.isLoading++;
				const denoisedBlob = await this.$Data.denoiseMedia({ azureFile: azureInfo, asBlob: true, fileName: fileName || azureInfo.FileName }).finally(() => this.isLoading--);
				azureInfo = await this.$azure.uploadBlobWithInfoResponse(`${this.$Data.uuid()}_denoised_${fileName || azureInfo.FileName}`, denoisedBlob);

				if (__DEBUG__)
					console.debug('createAudioInsertionFromAzureFile denoising', { denoisedBlob, azureInfo });
			}
			
			const insertion = (await this.$Data.createUpdatePureInsertion(audioInsertion))?.Data;
			insertion.Audio = azureInfo;
			let track = this.getTrackFromInsertion(insertion);
			this.processAddTrack(track);
			this.calcVisibleTracks();
			_.assign(audioInsertion, insertion);
			if (audioInsertion.Oid) {
				const payload = azureInfo;
				payload.ObjectId = audioInsertion.Oid;
				await Data.postExternalApi('Creative/SaveAzureAudioToInsertion', payload);
			}
			return track;
		},
		async createAudioInsertionInTypedTrack(opts, typedAggTrack) {
			let { azureInfo, insertionInfo, blob, fileName } = opts;

			let audioInsertion = {
				CreativeId: this.creative.Oid,
				Name: fileName || azureInfo.FileName,
				type: 'audioFile',
				JsonConfig: {
					trackNumber: typedAggTrack?.trackNumber
				},
				...(insertionInfo || {})
			};

			if (!azureInfo) {
				azureInfo = await this.$azure.uploadBlobWithInfoResponse(`${this.$Data.uuid()}_${fileName}`, blob);
			}
			
			const insertion = (await this.$Data.createUpdatePureInsertion(audioInsertion))?.Data;
			insertion.Audio = azureInfo;
			let track = this.getTrackFromInsertion(insertion);
			this.processAddTrack(track);
			this.calcVisibleTracks();
			_.assign(audioInsertion, insertion);
			if (audioInsertion.Oid) {
				const payload = azureInfo;
				payload.ObjectId = audioInsertion.Oid;
				if (__DEBUG__)
					console.debug('AUDIO 4', { payload });
				await Data.postExternalApi('Creative/SaveAzureAudioToInsertion', payload);
			}
			return track;
		},
		async createSsmlInsertionFromAudio(name, audio, opts) {
			if (__DEBUG__) {
				console.debug('createSsmlInsertionFromAudio start', { name, audio, opts });
			}
			try {
				this.isLoading++;
				let sourceBase64 = audio instanceof Blob ? await this.$Data.blob2Base64(audio) : null;
				const blob = audio instanceof Blob ? audio : null;

				if (opts?.denoiser) {
					this.isLoading++;
					sourceBase64 = await this.$Data.denoiseMedia({ base64: sourceBase64, fileName: name, azureFile: audio instanceof Blob ? null : audio }).finally(() => this.isLoading--);
				}

				const translationResult = await this.$Data.speechProcessAudio({
					Language: opts?.sourceLanguage?.locale,
					TranslateTo: opts?.targetLanguage?.locale,
					AzureFile: opts?.azureFile,
					AllSteps: true
				}, sourceBase64);

				if (!translationResult) return;

				const voices = _.orderBy(this.voices.filter(f => f.locale === opts?.targetLanguage?.locale && !f.isCustom), (v) => v.isNeural ? 0 : 1);
				const voice = voices[0] || this.voices.find(f => f.locale === 'en-US');

				const sourceVoices = _.orderBy(this.voices.filter(f => f.locale === opts?.sourceLanguage?.locale && !f.isCustom), (v) => v.isNeural ? 0 : 1);
				const sourceVoice = sourceVoices[0] || this.voices.find(f => f.locale === 'en-US');
				if (!voice) return;

				const originalSsml = ssmlHelper.prepareSsml(translationResult?.RecognizedText, {
					language: opts?.sourceLanguage?.locale,
					voice: sourceVoice?.id,
					rate: 1,
					pitch: 1
				})?.ssml;

				const ssml = ssmlHelper.prepareSsml(translationResult?.FinalText, {
					language: opts?.targetLanguage?.locale,
					voice: voice?.id,
					rate: 1,
					pitch: 1
				})?.ssml;

				if (__DEBUG__) {
					console.debug('createSsmlInsertionFromAudio translationResult', { translationResult, voice, ssml });
				}

				const originalAudio = opts?.azureFile || await this.$azure.uploadBlobWithInfoResponse(`${this.$Data.uuid()}_${name}`, blob);

				let insertion = {
					Oid: null,
					Type: 'SsmlInsertion',
					StartTime: opts?.startTime || 0,
					Ssml: ssml,
					Language: opts?.targetLanguage?.locale,
					OriginalSsml: originalSsml,
					Voice: voice?.id,
					Rate: 1,
					Pitch: 1,
					OriginalAudio: originalAudio,
					JsonConfig: {
						trackNumber: opts?.trackNumber ? opts?.trackNumber : this.$refs.tracker?.focusedAggregatedTrack?.trackNumber
					},
					CreativeId: this.creative?.Oid,
					...(opts?.insertionInfo || {})
				}

				const { buffer, base64 } = await ssmlGenerator.generateSSMLAudio(this.$refs.tracker, insertion, true);
				insertion.Duration = parseInt(buffer?.duration * 1000);
				insertion = (await this.$Data.createUpdatePureInsertion(insertion))?.Data;

				const insertionTrack = await this.$azure.uploadInsertionAudio(insertion, base64)
					.then(() => {
						let track = this.getTrackFromInsertion(insertion);
						track.src = base64;
						track.__isSrcLoaded = false;
						track.__isSubTrack = !!this.editingParentInsertion;

						this.processAddTrack(track);
						this.$refs.tracker.setBase64ForTrack(track, base64);
						this.calcVisibleTracks();

						return track;
					})
					.catch((ex) => {
						this.$notifyError(ex);
					})

				await this.createAudioInsertionInTypedTrack({
					fileName: name,
					azureInfo: originalAudio,
					blob,
					insertionInfo: {
						Name: (opts?.sourceLanguage?.name ? `(${opts?.sourceLanguage?.name}) ` : '') + (translationResult?.RecognizedText || translationResult?.FinalText || '').slice(0, 100) + '..',
						StartTime: opts?.startTime || 0,
					}
				}, this.getAggTrackByType('translation-source', {
					name: this.$tCaption('Translation sources'),
					muted: true
				}));

				if (__DEBUG__) {
					console.debug('createSsmlInsertionFromAudio final result', { translationResult, voice, ssml, insertion, originalAudio });
				}

				return insertionTrack;
			} catch(ex) {
				if (__DEBUG__) {
					console.error(ex);
				}
			} finally {
				this.isLoading--;
			}
		},
		async onNewVideoFileUpload(v, opts) {
			if (__DEBUG__)
				console.debug('onNewInsertionFileUpload', v);

			if (!v?.length) return;

			const fileBase64 = await this.$Data.blob2Base64(v[0]);
			const fileNameArr = `${v[0].name}`.split('.');
			fileNameArr.pop();
			const fileName = fileNameArr.join('.');

			const currentAggTracks = [...this.creative.JsonConfig.aggregatedTracks];
			
			this.isLoading++;
			try {
				const promises = [];
				const startTime = this.editingParentInsertion?.StartTime || parseInt(this.$refs.tracker.lastTime * 1000);
				const results = opts?.results || await videoHelper.splitVideo(fileBase64, v[0].name);

				if (__DEBUG__)
					console.debug('Splitted video', { results, fileName });

				if (results?.video?.FileName) {
					promises.push(this.createVideoInsertionFromAzureFile(fileName, results?.video, {
						StartTime: startTime,
						Duration: 0
					}));
				}
				
				if (results?.audio?.FileName) {
					const processExtracting = async () => {
						const azureAudios = await this.extractVoiceAndInstrumentals({ azureFile: results?.audio });

						if (azureAudios?.length) {
							await Promise.all((azureAudios || []).map(async (f, idx) => {
								const aggTrack = this.createNewAggregatedTrack();
								let name = f?.FileName?.indexOf('_vocals') === -1 ? '' : (f?.FileName?.indexOf('no_vocals') > -1 ? this.$tCaption('Instrumentals') : this.$tCaption('Vocals'))
								return this.createAudioInsertionFromAzureFile({
									insertionInfo: {
										StartTime: opts?.startTime || 0,
										Duration: 0,
										JsonConfig: {
											trackNumber: aggTrack?.trackNumber
										},
										Name: this.getNumberedInsertionName(name || this.$tCaption('Extracted audio'), !name ? idx : 0)
									},
									azureInfo: f,
									opts
								})
							}));
						}
					}
					if (opts?.isExtractingVoiceOrInstrumentals) {
						promises.push(processExtracting());
					}
					if (opts?.translate) {
						promises.push(this.createSsmlInsertionFromAudio(fileName, results?.audio, {
							...opts,
							azureFile: results?.audio,
							trackNumber: this.getAggTrackByType('video-audio', {
								name: this.$tCaption('Video: audio track')
							})?.trackNumber,
							insertionInfo: {
								Name: this.getNumberedInsertionName(this.$tCaption('Audio transcription'))
							}
						}));
					} else {
						promises.push(this.createVideoAudioInsertionFromAzureFile(fileName, results?.audio, {
							StartTime: startTime,
							Duration: 0,
							Name: this.getNumberedInsertionName(this.$tCaption('Audio for video'))
						}, {
							muted: !!opts?.isExtractingVoiceOrInstrumentals
						}, opts));
					}
				}

				if (promises.length) {
					await Promise.all(promises).then(tracks => {
						if (__DEBUG__)
							console.debug('New video tracks', { tracks });
						const finalTracks = tracks?.filter(f => f?.__link);
						if (finalTracks?.length > 1) {
							finalTracks[1].__link(finalTracks[0], { sourceLinkType: 'both' });
						}
					});
				}
			}
			finally {
				this.isLoading--;
				this.removeEmptyAggregatedTracks(currentAggTracks);
			}
		},
		onMediaFileSelected(v) {
			const file = v[0];
			this.fileUploading.url = null;
			if (!file) {
				this.fileUploading.selected = false;
				this.fileUploading.isVideo = false;
			}
			this.fileUploading.selected = true;
			this.fileUploading.isVideo = file && (file.name || '').endsWith('mp4');
		},
		async extractVoiceAndInstrumentals(opts) {
			const azureSettings = await this.$Data.getAzureSettings();
			const demucsUrl = this.$auth.userInfo?.user?.DemucsAzureFunctionUrl;
			const sasToContainer = (await this.$azure.getSasForAzureContainer(void 0, 300));

			let url = opts?.url;

			if (opts?.azureFile) {
				const sas = (await this.$azure.getSasForAzureContainer(opts?.azureFile?.Container, 300))?.sasKey;
				url = opts?.azureFile.AzureFileUrl + `?${sas}`;
			}

			if (opts?.blob) {
				const name = `${Data.uuid()}_${`${opts?.blob?.name}`.replace(/[() &]/g, '_') || 'noname.wav'}`;
				const azureBlob = await this.$azure.uploadBlobWithInfoResponse(name, opts?.blob, 'temporary');
				
				const sas = (await this.$azure.getSasForAzureContainer('temporary', 300))?.sasKey;
				url = azureBlob?.AzureFileUrl + `?${sas}`;
			}
			
			if (__DEBUG__)
				console.debug('extractVoiceAndInstrumentals init', { opts, demucsUrl });

			const prepareAzureContainer = (res) => {
				if (!res) return null;

				const fileUrl = res.split('?sv')[0];
				const fileName = fileUrl.split(sasToContainer.containerName + '/').slice(-1)[0];
				return {
					StorageType: 2,
					AzureAccountOid: azureSettings.StorageAccount.Oid,
					AzureContainer: sasToContainer.containerName,
					AzureFileUrl: fileUrl,
					FileName: fileName,
					FileSize: 1
				}
			}

			const result = await this.$azure.postDurableFunction(demucsUrl, {
				Command: " -n htdemucs --two-stems=vocals [file.mp3]",
				SasToContainer: sasToContainer?.sas,
				InputFiles: [
					{
						"FileName": "file.mp3",
						"URL": url
					}
				]
			}).loaded().then(data => {
				const files = data?.outputs || data?.outputFiles || [];
				if (!files?.length) throw new Error(config.getVueApp().$tCaption('Errors occured during operation.', '@Creative'));
				return files;
			})

			const azureFiles = result.map(f => prepareAzureContainer(f));

			if (__DEBUG__)
				console.debug('extractVoiceAndInstrumentals result', { result, azureFiles });

			return azureFiles;
		},
		removeEmptyAggregatedTracks(oldTracks) {
			if (__DEBUG__)
				console.debug('removeEmptyAggregatedTracks', { oldTracks });
			if (this.creative.JsonConfig.aggregatedTracks.length > 1 && oldTracks[0]?.__tracks?.length === 0) {
				const idx = this.creative.JsonConfig.aggregatedTracks.findIndex(f => f.trackNumber === oldTracks[0].trackNumber);
				if (idx > -1) {
					this.creative.JsonConfig.aggregatedTracks.splice(idx, 1);
				}
			}
		},
		getNumberedInsertionName(name) {
			let length = (this.allTracks || []).filter(f => f?.insertion?.Name && f.insertion.Name.startsWith(name)).length;
			return `${name} ${length + 1}`;
		},
		async onNewMediaFileDownloadUrl(url, opts) {
			const startTime = this.editingParentInsertion?.StartTime || parseInt(this.$refs.tracker.lastTime * 1000);
			const currentAggTracks = [...this.creative.JsonConfig.aggregatedTracks];
			if (this.downloadUrlIsVimeoOrYoutube) {
				this.isLoading++;
				const results = await this.$Data.grabMedia(url || this.fileUploading.downloadUrl, {
					GrabAudio: this.fileUploading.grabAudio || opts?.isExtractingVoiceOrInstrumentals,
					GrabVideo: this.fileUploading.grabVideo
				}).finally(() => this.isLoading--);

				const promises = [];

				if (__DEBUG__)
					console.debug('onNewMediaFileDownloadUrl', { results, opts });

				if (results?.video) {
					promises.push(this.createVideoInsertionFromAzureFile(void 0, results?.video, {
						StartTime: startTime,
						Duration: 0,
						Name: results?.title ? `${results?.title}, ${this.$tCaption('video')}` : this.getNumberedInsertionName(this.$tCaption('Video'))
					}));
				}
				
				if (results?.audio) {
					const processExtracting = async () => {
						const azureAudios = await this.extractVoiceAndInstrumentals({ azureFile: results?.audio });

						if (azureAudios?.length) {
							await Promise.all((azureAudios || []).map(async (f, idx) => {
								let aggTrack = (this.isSonicScribe ? this.creative?.JsonConfig?.aggregatedTracks[0] : null) || this.createNewAggregatedTrack();
								let name = f?.FileName?.indexOf('_vocals') === -1 ? '' : (f?.FileName?.indexOf('no_vocals') > -1 ? this.$tCaption('Instrumentals') : this.$tCaption('Vocals'))
								return this.createAudioInsertionFromAzureFile({
									insertionInfo: {
										StartTime: opts?.startTime || 0,
										Duration: 0,
										JsonConfig: {
											trackNumber: aggTrack?.trackNumber
										},
										Name: results?.title ? `${results?.title}, ${this.$tCaption('extracted audio')}` : this.getNumberedInsertionName(name || this.$tCaption('Extracted audio'), !name ? idx : 0)
									},
									azureInfo: f,
									opts
								})
							}));
						}
					}
					if (opts?.isExtractingVoiceOrInstrumentals) {
						promises.push(processExtracting());
					}

					if (opts?.translate) {
						promises.push(this.createSsmlInsertionFromAudio(void 0, results?.audio, {
							...opts,
							azureFile: results?.audio,
							trackNumber: this.getAggTrackByType('video-audio', {
								name: this.$tCaption('Video: audio track')
							})?.trackNumber,
							insertionInfo: {
								Name: this.getNumberedInsertionName(this.$tCaption('Audio transcription'))
							}
						}));
					} else {
						promises.push(this.createVideoAudioInsertionFromAzureFile(void 0, results?.audio, {
							StartTime: startTime,
							Duration: 0,
							Name: results?.title ?  `${results?.title}, ${this.$tCaption('audio')}` : this.getNumberedInsertionName(this.$tCaption('Audio for video'))
						}, {
							muted: !!opts?.isExtractingVoiceOrInstrumentals
						}, opts));
					}
				}

				if (promises.length) {
					this.isLoading++;
					await Promise.all(promises).then(tracks => {
						if (__DEBUG__)
							console.debug('New video/audio tracks', { tracks });
						const finalTracks = tracks?.filter(f => f?.__link);
						if (finalTracks?.length > 1) {
							finalTracks[1].__link(finalTracks[0], { sourceLinkType: 'both' });
						}
					}).finally(() => this.isLoading--);
				}
			} else {
				const processResult = async (data) => {
					if (__DEBUG__)
						console.debug('onNewMediaFileDownloadUrl', data);
					if (data?.size) {
						if (`${data.type}`.startsWith('video')) {
							await this.onNewVideoFileUpload([data], opts);
						} else {
							await this.onNewInsertionFileUpload([data], opts);
						}
					}
				};
				this.isLoading++;
				await axios.get(this.fileUploading.downloadUrl, { responseType: 'blob' }).then(({ data }) => processResult(data)).catch(async () => {
					this.isLoading++;
					await this.$Data.proxyDownload(this.fileUploading.downloadUrl, { responseType: 'blob' })
						.then(processResult)
						.catch(() => this.$notifyError(this.$tCaption('Can\'t download media file')))
						.finally(() => this.isLoading--);
				}).finally(() => this.isLoading--);
			}
			this.removeEmptyAggregatedTracks(currentAggTracks);
		},
		async onNewMediaFileUpload(v) {
			const opts = _.cloneDeep(this.fileUploading) || {};
			opts.startTime = this.editingParentInsertion?.StartTime || parseInt(this.$refs.tracker.lastTime * 1000);
			opts.isExtractingVoiceOrInstrumentals = this.isExtractingVoiceOrInstrumentals;
			opts.denoiser = this.fileUploading?.denoiser || false;
			try {
				this.$notifyMessage(this.$tCaption('Processing media: <MEDIANAME>').replace(/<MEDIANAME>/g, v && v[0]?.name || this.fileUploading.downloadUrl), { busy: true, type: 'info' });
				if (this.fileUploading.downloadUrl) {
					await this.onNewMediaFileDownloadUrl(this.fileUploading.downloadUrl, opts);
				} else {
					if (this.fileUploading.isVideo) {
						await this.onNewVideoFileUpload(v, opts);
					} else {
						await this.onNewInsertionFileUpload(v, opts);
					}
				}
				this.$notifyMessage(this.$tCaption('Media "<MEDIANAME>" processed.').replace(/<MEDIANAME>/g, v && v[0]?.name || this.fileUploading.downloadUrl), { busy: false, type: 'success' });
			} catch(ex) {
				console.error(ex);
				this.$notifyMessage(this.$tCaption(`Media "<MEDIANAME>" processed with errors. ${ex}`).replace(/<MEDIANAME>/g, v && v[0]?.name || this.fileUploading.downloadUrl), { busy: false, type: 'error' });
			}
		},
		onMediaFileRemoved() {
			this.fileUploading.selected = false;
			this.fileUploading.isVideo = false;
		},
		onNewAudioRecorded(base64, title, startPosition, fileUploading) {
			return this.onNewBase64FileData(base64, `${title || 'Voice record'}.wav`, { StartTime: parseInt(startPosition * 1000) }, { fileUploading });
		},
		onNewAudioRecordedSubTrack(parentTrack, base64, fileName, customData, opts) {
			return this.onNewBase64FileData(base64, `${fileName || 'Record'}.wav`, { ParentId: parentTrack?.id, ...(customData || {}) }, { preventEditingTrack: true, ...(opts || {}) });
		},
		async onNewBase64FileData(base64, fileName, customData, opts) {
			if (__DEBUG__)
				console.debug('onNewBase64FileData', { fileName, customData, opts });
			const editingTrack = opts?.editingTrack || (opts?.preventEditingTrack ? null : this.editingTrack);
			const fileUploading = opts?.fileUploading || this.fileUploading;

			if (opts?.denoiser) {
				this.isLoading++;
				base64 = await this.$Data.denoiseMedia({ base64, fileName }).finally(() => this.isLoading--);
			}

			return new Promise(resolve => {
				let blob = this.$Data.dataURItoBlob(base64);

				this.$Data.getAudioBufferFromBlob(blob).then(buffer => {
					let duration = buffer.duration;
					let name = `${fileName}`.split('.').slice(0, -1).join('.') || fileName;
					const editingParentInsertion = customData?.ParentId && this.loadedTracks.find(f => f.id === customData?.ParentId)?.insertion || this.editingParentInsertion;
					let insertion = {
						Oid: editingTrack?.id || null,
						Name: name || this.getNumberedInsertionName(this.$tCaption('Audio')),
						StartTime: editingParentInsertion?.StartTime || parseInt(this.$refs.tracker.lastTime * 1000),
						Duration: parseInt(duration * 1000),
						CreativeId: this.creative.Oid,
						ParentId: editingParentInsertion?.Oid,
						JsonConfig: {
							trackNumber: opts?.trackNumber !== void 0 ? opts.trackNumber : this.$refs.tracker?.focusedAggregatedTrack?.trackNumber
						},
						type: 'audioFile',
						...(customData || {})
					};

					if (__DEBUG__)
						console.debug('Audio locally loaded', { insertion, editingParentInsertion, fileUploading, opts });

					let uploadAudio = (insertion, base64) => {
						let track = editingTrack || this.getTrackFromInsertion(insertion);
						if (!editingTrack) {
							track.__isSubTrack = !!editingParentInsertion;
							this.processAddTrack(track);
						}
						this.calcVisibleTracks();
						track.__setBase64(base64);
						track.__isLoading++;
						return (new Promise(mainResolve => {
							if (__DEBUG__)
								console.debug('Upload creative audio', { base64 });
							let toolPromise = (method, opts, condition, audioBase64) => new Promise(resolve => {
								this.isLoading++;
								if (condition) {
									let data = audioBase64.split(';base64,')[1];
									let ext = Data.getBase64AudioExtension(audioBase64);
									this.$Data.postExternalApi(`audio/${method}`, {
										MediaExtension: ext,
										...opts,
										Base64Content: data
									}, {
										xhr: () => {
											let xhr = new XMLHttpRequest();
											xhr.responseType = 'blob';
											return xhr;
										},
										processData: false,
										dataType: 'binary'
									}).then(r => {
										if (r.type === 'audio/wav') {
											let fr1 = new FileReader;
											fr1.onloadend = () => {
												if (__DEBUG__)
													console.debug(`${method} result on file upload`, { r: fr1.result });
												resolve(fr1.result);
											};
											fr1.readAsDataURL(r);
										} else {
											let fr1 = new FileReader;
											fr1.onloadend = () => {
												const json = JSON.parse(fr1.result);
												if (json?.ErrorMessage) {
													this.$notifyError(json.ErrorMessage);
												}
												if (__DEBUG__)
													console.error(`${method} result on file upload`, { json });
											};
											fr1.readAsText(r);
											resolve(audioBase64);
										}
									}).catch(() => {
										resolve(audioBase64);
									}).finally(() => this.isLoading--);
								} else {
									this.isLoading--;
									resolve(audioBase64);
								}
							});

							let promise = null;
							if (fileUploading.trim && fileUploading.normalize) {
								promise = toolPromise('TrimAndNormalizeAudio', {
									TrimLevel: fileUploading.trimDbLevel,
									NormalizeLevel: fileUploading.normalizeDbLevel
								}, true, base64);
							} else if (fileUploading.trim || fileUploading.normalize) {
								promise = fileUploading.trim
									? toolPromise('TrimAudio', { DbLevel: fileUploading.trimDbLevel }, true, base64)
									: toolPromise('NormalizeAudio', { DbLevel: fileUploading.normalizeDbLevel }, true, base64);
							} else {
								promise = Promise.resolve(base64)
							}

							track.__isLoading++;
							promise.then(r2 => {
								mainResolve(r2);
								this.isLoading++;
								(this.inMemory ? Promise.resolve(track) : (this.$azure.uploadInsertionAudio(insertion, r2, void 0, ({ percent }) => {
									track.__isSrcLoaded = false;
									track.__loadedPercentage = percent;
								}))).then(() => {
									if (__DEBUG__)
										console.debug('Audio insertion uploaded', { r2 });

									track.__setBase64(r2);
									this.calcVisibleTracks();

									resolve(track);
								})
								.catch((ex) => {
									this.$notifyError(ex);
								})
								.finally(() => this.isLoading--);
								return r2;
							}).finally(() => track.__isLoading--);
						})).finally(() => track.__isLoading--);
					};

					if (editingTrack) {
						editingTrack.startOffset = 0;
						editingTrack.endOffset = 0;
						uploadAudio(editingTrack?.insertion, base64).then(r => {
							this.$refs.tracker.setBase64ForTrack(editingTrack, r);
						});
					} else {
						this.isLoading++;
						if (__DEBUG__)
							console.debug('onNewBase64FileData');
						this.$Data.createUpdatePureInsertion(insertion).then(r => {
							if (__DEBUG__)
								console.debug('Audio insertion cretead', { r });
							if (r.Success) {
								insertion.Oid = r.Data.Oid;
								uploadAudio(insertion, base64);
							} else {
								this.$notifyError(r.ErrorMessage);
								resolve(null);
							}
							this.isLoading--;
							return null;
						}).catch(() => { 
							this.isLoading--;
							resolve(null);
						});

					}
				});
			});
		},
		editInsertion(track) {
			if (__DEBUG__)
				console.debug('Edit insertion', { track });

			let sourceTrack = typeof(track) === 'object' ? (track?.sourceTrack || track) : this.allTracks.find(f => f.id === track);
			if (__DEBUG__)
				console.debug('Edit insertion', { sourceTrack });
			if (!sourceTrack || !sourceTrack.__isInsertionLoaded) return;
			this.editingTrack = sourceTrack;
			switch (sourceTrack.type) {
				case 'audioFile':
					if (this.readonly) return;
					this.$refs.insertionAudioUploader.showPopup();
					break;
				case 'video':
					if (this.readonly) return;
					this.$refs.insertionVideUploader.showPopup();
					break;
				case 'textToSpeech':
					this.$refs.insertionEditor.showInsertion();
					break;
				case 'break':
					this.breakPopupVisible = false;
					this.$nextTick(() => this.breakPopupVisible = true);
					break;
			}
		},
		adjustSsmlInsertionRateByDuration(track, v) {
			const sourceDuration = track.insertion.Duration * 0.001 * (track.insertion?.Rate || 1);
			const newRate = Math.min(2, Math.max(0.5, sourceDuration / v));
			const newVisualDuration = sourceDuration / newRate;
			this.$set(track.insertion, 'Rate', newRate);
			this.$set(track.insertion, 'Duration', parseInt(newVisualDuration * 1000));
			track.__saveInsertion('adjustSsmlInsertionRateByDuration', { regenerate: true });
		},
		getTrackFromInsertion(f, needLoading, additional) {
			if (!f) return;

			if (__DEBUG__)
				console.debug('getTrackFromInsertion'/*, { insertion: f, needLoading, additional }*/);

			if (!f.eventRuleId)
				f.eventRuleId = f.EventRule && f.EventRule.Oid || null;
			if (!f.Ssml)
				f.Ssml = null;
			if (!f.Targets)
				f.Targets = [];
			f.JsonConfig = _.merge({
				trackNumber: 0,
				hasDefaultVoiceReplaced: false,
				parentStartDelta: 0,
				adjustSubTrackLengthToParent: false,
				fadeIn: { value: 0, type: 'exponential' },
				fadeOut: { value: 0, type: 'exponential' },
				volumeEnvelope: {
					points: [],
					enabled: false
				},
				pitchEnvelope: {
					points: [],
					enabled: false
				}
			}, f.JsonConfig || {});

			const type = this.getTypeFromInsertion(f);

			const loadTypes = ['textToSpeech', 'audioFile', 'break'];

			let track = {
				id: f.Oid,
				insertion: f,
				subTracks: [],
				type,
				__isSubTrack: !!f.ParentId,
				__isInsertionLoaded: !needLoading || !loadTypes.includes(type),
				__internallyMuted: false,
				__allowPublishing: false,
				__isDeleted: false,
				__skipPages: 0,
				__skipSubTracks: 0,
				__groups: [],
				__totalSubInsertions: f.CountOfSubInsertions || 0,
				__countOfAllSubInsertions: f.CountOfSubInsertions || 0,
				__totalFilteredSubInsertions: f.CountOfSubInsertions || 0,
				__totalLoadingItemsCount: 0,
				__canvasWidth: 0,
				__canvasLeft: 0,
				__isGeneratingSubInsertions: f?.LastProcessingJob && f?.LastProcessingJob?.Status <= 2,
				__isSaving: 0
			};
			
			const htmlText = (html) => {
				const div = document.createElement('div');
				div.innerHTML = html;
				return div.textContent;
			}
			const nameFunc = () => track?.insertion?.Ssml ? (track.insertion.Ssml && htmlText(track.insertion.Ssml)) : track.insertion.Name;				
			Object.defineProperty(track, 'name', {
				get: () => nameFunc
			});

			if (!f.hasOwnProperty('__track')) {
				const getInsertionTrack = () => this.allTracks.find(t => t.id === f.Oid);
				Object.defineProperty(f, '__track', {
					get: () => getInsertionTrack,
					enumerable: true
				});
			}

			Object.defineProperty(track, '__duplicate', {
				get: () => this.duplicateTrack
			});

			const hasDynamicData = () => {
				const dynamicTags = ['adthos:wbs', 'adthos:variativetext'];
				return track?.insertion?.Ssml && dynamicTags.some(f => track?.insertion?.Ssml?.indexOf(f) > -1);
			}
			Object.defineProperty(track, '__hasDynamicData', {
				get: () => hasDynamicData
			});

			let marker = null;
			Object.defineProperty(track, '__marker', {
				get: () => marker || track?.insertion?.NetworkNetCue?.Name,
				set: (v) => marker = v
			});
			
			let isLoading = false;
			Object.defineProperty(track, '__isLoading', {
				get: () => isLoading,
				set: (v) => {
					isLoading = Math.max(0, v);
					this.onTrackLoading();
				},
				enumerable: true
			});

			Object.defineProperty(track, '__isBusy', {
				get: () => track.__isLoading || track.__isGeneratingSubInsertions
			});
			
			Object.defineProperty(track, 'parentId', {
				get: () => f.ParentId,
				set: (v) => f.ParentId = v,
				enumerable: true
			});

			const isServersideCalculated = () => !!track.insertion?.EventRule?.Oid || !!track.insertion?.Targets?.length;
			Object.defineProperty(track, '__isServersideCalculated', {
				get: () => isServersideCalculated
			});

			const trackNumber = () => {
				if (f.ParentId) {
					return track.__parentTrack?.trackNumber();
				} else {
					let trackNumber = track?.insertion?.JsonConfig?.trackNumber;
					if (!this.creative?.JsonConfig?.aggregatedTracks?.filter(t => t.trackNumber === trackNumber)[0]) {
						trackNumber = this.creative?.JsonConfig?.aggregatedTracks[0]?.trackNumber;
					}
					return trackNumber || 0;
				}
			}
			Object.defineProperty(track, 'trackNumber', {
				get: () => trackNumber
			});

			Object.defineProperty(track, 'playbackRate', {
				get: () => track?.insertion?.JsonConfig?.playbackRate || 1,
				set: (v) => {
					config.getVueApp().$set(track?.insertion?.JsonConfig, 'playbackRate', v);
					track.__preProcessSourceBuffer();
				},
				enumerable: true
			});

			Object.defineProperty(track, 'pitchRate', {
				get: () => track?.insertion?.JsonConfig?.pitchRate || 1,
				set: (v) => {
					config.getVueApp().$set(track?.insertion?.JsonConfig, 'pitchRate', v);
					track.__preProcessSourceBuffer();
				},
				enumerable: true
			});

			Object.defineProperty(track, 'volumeRate', {
				get: () => track?.insertion?.JsonConfig?.volumeRate || 1,
				set: (v) => config.getVueApp().$set(track?.insertion?.JsonConfig, 'volumeRate', v),
				enumerable: true
			});

			Object.defineProperty(track, 'clipMuted', {
				get: () => track?.insertion?.JsonConfig?.clipMuted || false,
				set: (v) => config.getVueApp().$set(track?.insertion?.JsonConfig, 'clipMuted', v),
				enumerable: true
			});

			let src = f?.__src || ((asBlob, onProgress) => this.$azure.getInsertionAudio(f, asBlob, onProgress));
			Object.defineProperty(track, 'src', {
				get: () => src,
				set: (v) => src = v,
				enumerable: true
			});
			
			let nameHeader = (track) => track?.insertion?.Ssml ? track.insertion.Name : '';
			Object.defineProperty(track, 'nameHeader', {
				get: () => nameHeader,
				set: (v) => nameHeader = v
			});
			
			let getTrimmedDuration = (preventOffsets) => Math.max(0, parseFloat(parseFloat(((track.duration - (preventOffsets ? 0 : (track.startOffset + track.endOffset)) || 0) / 1)).toFixed(3)));
			Object.defineProperty(track, 'getTrimmedDuration', {
				get: () => getTrimmedDuration,
				set: (v) => getTrimmedDuration = v
			});

			Object.defineProperty(track, 'trimmedDuration', {
				get: () => track.getTrimmedDuration(void 0),
				set: () => {},
				enumerable: true
			});

			Object.defineProperty(track, 'breakType', {
				get: () => track.insertion.BreakType !== null && this.breakTypes[track.insertion.BreakType]?.id,
				set: (v) => track.insertion.BreakType = this.breakTypes.findIndex(f => f.id === v),
				enumerable: true
			});

			Object.defineProperty(track, '__isAudioTrack', {
				get: () => !track.breakType && track.type !== 'video',
			});

			Object.defineProperty(track, '__isVideoTrack', {
				get: () => track.type === 'video',
			});

			Object.defineProperty(track, '__supportsResizing', {
				get: () => !track.breakType && !(track.type === 'textToSpeech' && this.pressedKeys?.includes(17)),
			});

			Object.defineProperty(track, '__supportsDurationChange', {
				get: () => ['textToSpeech', 'break'].includes(track.type),
			});

			Object.defineProperty(track, '__isAudioEditableTrack', {
				get: () => !track.breakType && track.type !== 'textToSpeech' && track.type !== 'video',
			});

			Object.defineProperty(track, 'start', {
				get: () => {
					return Math.max(0, !track.__isSubTrack ? track.insertion.StartTime * 0.001 : ((track.__parentTrack?.start || 0) + (track.insertion.JsonConfig.parentStartDelta || 0)));
				},
				set: (v) => {
					if (!track.__isSubTrack) {
						track.__processLinkedTracks({ delta: v - track.start, changed: 'start' });
						track.insertion.StartTime = parseInt(v * 1000);
					} else {
						track.insertion.JsonConfig.parentStartDelta = v - (track.__parentTrack?.start || 0);
					}
				}
			});

			Object.defineProperty(track, 'end', {
				get: () => {
					return track.start + track.trimmedDuration;
				}
			});

			Object.defineProperty(track, 'startOffset', {
				get: () => track.insertion.StartOffset * 0.001 || 0,
				set: (v) => {
					track.__processLinkedTracks({ delta: v - track.insertion.StartOffset * 0.001, changed: 'startOffset' });
					this.$set(track.insertion, 'StartOffset', parseInt(v * 1000) || null);
				},
				enumerable: true
			});

			Object.defineProperty(track, 'endOffset', {
				get: () => track.insertion.EndOffset * 0.001 || 0,
				set: (v) => {
					track.__processLinkedTracks({ delta: v - track.insertion.EndOffset * 0.001, changed: 'endOffset' });
					this.$set(track.insertion, 'EndOffset', parseInt(v * 1000) || null);
				},
				enumerable: true
			});

			Object.defineProperty(track, 'duration', {
				get: () => track.insertion.Duration * 0.001 || 0,
				set: (v) => this.$set(track.insertion, 'Duration', parseInt(v * 1000) || null),
				enumerable: true
			});

			const updateDuration = (v) => {
				if (track.type === 'textToSpeech') {
					this.adjustSsmlInsertionRateByDuration(track, v);
				} else {
					this.$set(track.insertion, 'Duration', parseInt(v * 1000) || null);
				}
			}
			Object.defineProperty(track, 'updateDuration', {
				get: () => updateDuration,
				enumerable: true
			});

			Object.defineProperty(track, '__aggregatedTrack', {
				get: () => {
					return this.creative?.JsonConfig?.aggregatedTracks.filter(t => t.trackNumber === track.trackNumber())[0] || this.creative?.JsonConfig?.aggregatedTracks[0];
				}
			});

			Object.defineProperty(track, '__trackNumber', {
				get: () => {
					return track.trackNumber();
				}
			});

			Object.defineProperty(track, '__parentTrack', {
				get: () => {
					return track.__isSubTrack && track.__tracker.tracks.filter(f => f.id === track.insertion.ParentId)[0] || null;
				}
			});

			Object.defineProperty(track, '__isMuted', {
				get: () => {
					if (this.isPublishing) {
						return !track.__allowPublishing;
					}
					return !this.$refs.tracker?.getTrackCanPlayed(track?.id);
				}
			});

			Object.defineProperty(track, '__isSolo', {
				get: () => {
					if (this.isPublishing) {
						return false;
					}
					let soloTrackId = this.$refs.tracker?.soloTrackId;
					let soloSubTrackIds = this.$refs.tracker?.soloSubTrackIds;

					return (!track.__isSubTrack && soloTrackId !== null && (soloTrackId === track?.id || soloTrackId === track?.__aggregatedTrack.trackNumber))
						|| (track.__isSubTrack && soloSubTrackIds !== null && soloSubTrackIds?.includes(track?.id));
				}
			});

			Object.defineProperty(track, 'allowPublishing', {
				get: () => {
					if (this.isPublishing) {
						return track.__allowPublishing;
					}
					return null;
				}
			});

			let ensured = false;
			let ensureTrackLoaded = () => {
				if (!ensured)
					return this.checkSsmlTrack(track).then(() => {
						this.checkSimulateRules(track.__parentTrack || track);
						return true;
					});
				ensured = true;
				return Promise.resolve(true);
			}
			Object.defineProperty(track, '__ensureTrackLoaded', {
				get: () => ensureTrackLoaded
			});

			let subTracksSearchString = null;
			Object.defineProperty(track, '__subTracksSearchString', {
				get: () => subTracksSearchString,
				set: (v) => {
					subTracksSearchString = v;
					track.__skipPages = 0;
					track.__loadSubTracks(true);
				}
			});

			let addSubTracksSkip = (delta) => {
				if (__DEBUG__)
					console.debug('addSubTracksSkip', { delta, skipped: track?.__skipSubTracks, __total: track?.__totalFilteredSubInsertions, track });
				if (track?.__totalFilteredSubInsertions) {
					let newValueWithDelta = track.__skipSubTracks + (delta || 0);
					let newValue = Math.max(0, Math.min(track?.__totalFilteredSubInsertions - this.$refs.tracker?.subTracksVisibleLimit, newValueWithDelta));
					let limit = this.$refs.tracker?.subTracksVisibleLimit;
					if (newValue !== track.__skipSubTracks) {
						track.__skipSubTracks = newValue;
						track.__skipPages = Math.max(0, Math.floor(track.__skipSubTracks / limit));
						track.__loadSubTracks();
						if (__DEBUG__)
							console.debug('Add sub tracks skip', { delta, newValue });
						return true;
					}
					if (__DEBUG__)
						console.debug('Add sub tracks skip', { delta, newValue, old: track.__skipSubTracks });
				}
				return false;
			};

			Object.defineProperty(track, 'addSubTracksSkip', {
				get: () => addSubTracksSkip
			});

			let subTracksInited = track.__totalSubInsertions === 0;
			const initSubTracks = () => {
				if (!subTracksInited && track.__totalSubInsertions > 0) {
					subTracksInited = true;
					track.__loadSubTracks();
				}
			}
			Object.defineProperty(track, '__initSubTracks', {
				get: () => initSubTracks
			});
		
			Object.defineProperty(track, '__limitPeriod', {
				get: () => {	
					let item = track.insertion;

					let hints = [];

					if (item.ActiveFrom && item.ActiveTo) {
						let periods = [];
						if (item.ActiveFrom) {
							periods.push(this.$tCaption('from DATE').replace(/DATE/g, this.$moment(item.ActiveFrom).format('L')));
						}
						
						if (item.ActiveTo) {
							periods.push(this.$tCaption('to DATE').replace(/DATE/g, this.$moment(item.ActiveTo).format('L')));
						}
		
						if (item.Targets?.length) {
							if (item.Targets.length === 1) {
								periods.push(this.$tCaption('in the TARGET time zone').replace(/TARGET/g, item.Targets[0].Name));
							} else {
								periods.push(this.$tCaption('in the geo-target time zone'));
							}
						} else {
							periods.push(this.$tCaption('in the creative\'s time zone'));
						}
		
						hints.push(this.$tCaption('Active PERIODS').replace(/PERIODS/g, periods.join(' ')));
					}

					return hints.join(', ');
				}
			});

			let lastLoadSubTrackSessionId = null;
			let loadSubTracks = (reload) => {
				const prevTracks = track.subTracks;
				track.subTracks = [];
				if (reload || !track.subTracks) {
					track.__skipSubTracks = 0;
					track.__totalLoadingItemsCount = 0;
				}
				if (this.isPublishMode || this.isPublishing || !track.id) return Promise.resolve(null);
				track.__totalLoadingItemsCount = Math.max(track.__totalLoadingItemsCount, track.__skipSubTracks + this.$refs.tracker?.subTracksVisibleLimit);
				track.__isLoading++;
				subTracksInited = true;

				const loadSubTrackSessionId = this.$Data.uuid();
				lastLoadSubTrackSessionId = loadSubTrackSessionId;
				const searchString = subTracksSearchString ? `&search=${subTracksSearchString}` : '';
				const rulesString = this.simulatedItems?.ruleIds?.length ? `&ruleIds=${this.simulatedItems?.ruleIds.join(',')}` : '';
				const targetsString = this.simulatedItems?.targetIds?.length ? `&targetIds=${this.simulatedItems?.targetIds.join(',')}` : '';
				return Data.getExternalApi('creative/GetChildrenInsertionsPaged', `parentInsertionId=${track.id}&page=${track.__skipPages + 1}${searchString}${rulesString}${targetsString}`, this.$refs.tracker?.subTracksVisibleLimit).then(r => {
					if (lastLoadSubTrackSessionId !== loadSubTrackSessionId) return;
					let results = r.Results.filter(f => !track.subTracks.some(st => st.id === f.Oid));
					if (__DEBUG__)
						console.debug('Sub insertions loaded', { r, results, tIds: track.subTracks.map(f => f.id), rIds: r.Results.map(f => f.Oid) });
					track.insertion.SubInsertions.push(...results);
					if (!subTracksSearchString)
						track.__totalSubInsertions = r.TotalNumberOfRecords;
					track.__totalFilteredSubInsertions = r.TotalNumberOfRecords;
					track.subTracks.push(...results.map(s => {
						let subTrack = this.getTrackFromInsertion(s, true,  {
							insertion: {
								ParentId: track.id
							},
							__isSubTrack: true,
							//__isInsertionLoaded: true //TODO: fix Ssml data (Need to be loaded in GetSubTracks method all data)
						});
						let prevTrack = prevTracks.find(f => f.id === subTrack.id);
						if (prevTrack) {
							_.merge(prevTrack, subTrack);
							return prevTrack;
						}
						subTrack.__ensureTrackLoaded();
						return subTrack;
					}));
					this.checkSimulateRules(track);
					if (track.subTracks.length)
						this.calcVisibleTracks();
					track.__tracker?.loadData(track.subTracks).finally(() => track.__tracker?.updateTracksList());
				}).finally(() => track.__isLoading--);
			}
			Object.defineProperty(track, '__loadSubTracks', {
				get: () => loadSubTracks
			});

			let loadSubTrack = (id) => {
				let subTrack = track.subTracks.find(f => f.id === id);
				if (!subTrack) {
					track.__isLoading++;
					return new Promise(res => Data.postExternalApi('creative/GetInsertionByIds', [id]).then(r => {
						if (__DEBUG__)
							console.debug('Sub insertion loaded', { r });
						let results = r.filter(f => !track.subTracks.some(st => st.id === f.Oid));
						track.insertion.SubInsertions.push(...results);
						track.subTracks.push(...results.map(s => {
							let subTrack = this.getTrackFromInsertion(s, true, {
								insertion: {
									ParentId: track.id
								},
								__isSubTrack: true
							});
							return subTrack;
						}));
						this.checkSimulateRules(track);
						if (track.subTracks.length)
							this.calcVisibleTracks();

						let subTrack = track.subTracks.find(f => f.id === id);
						return this.checkSsmlTrack(subTrack).then(() => {
							if (!subTrack?.insertion?.Audio)
								return ssmlGenerator.generateAndSaveSSMLAudio(this, subTrack).then(() => res(subTrack));
							return this.$refs.tracker.loadData([subTrack]).then(() => res(subTrack));
						});
					}).finally(() => track.__isLoading--));
				} else {
					return Promise.resolve(subTrack);
				}
			}

			Object.defineProperty(track, '__loadSubTrack', {
				get: () => loadSubTrack
			});

			const regenerate = () => {
				if (__DEBUG__)
					console.debug('Regenerate');
				if (this.isPublishing || this.isPublished) return;
				return Promise.all([
					this.$Data.createUpdatePureInsertion(track.insertion),
					ssmlGenerator.generateAndSaveSSMLAudio(this, track)
				]);
			};
			Object.defineProperty(track, '__regenerate', {
				get: () => regenerate
			});

			const saveInsertion = _.debounce((log, opts, force) => {
				if (this.isPublishing || this.isPublished) return;
				if (__DEBUG__)
					console.debug('Save insertion', { log, opts, isPublishing: this.isPublishing, isPublished: this.isPublished });
				if (opts?.regenerate) {
					ssmlGenerator.generateAndSaveSSMLAudio(this, track);
				}
				this.saveTrackInsertion(track, log, force);
			}, 1000, {
				leading: false,
				trailing: true
			});

			Object.defineProperty(track, '__saveInsertion', {
				get: () => saveInsertion
			});

			const recognizeSpeech = async (force) => {
				let processedInfo = track?.insertion?.JsonConfig?.recognizedSpeech;

				if (track?.__buffer) {
					const isSame = processedInfo?.bufferLength === track?.__buffer?.length && !force && processedInfo?.result
						&& track.startOffset === processedInfo?.startOffset && track.endOffset === processedInfo?.endOffset;
					if (!isSame) {
						if (this.isSonicScribe) {
							const result = await this.$Data.speechProcessAudio({
								Language: this.sonicScribeLanguage || track.__tracker.language || 'en-US',
								TranslateTo: null,
								ProfileCode: this.sonicScrbieSTTProfile || 'SonicScribe',
								AllSteps: true,
								AzureFile: track.insertion.Audio
							}).finally(() => this.isProcessing--);

							this.$set(track.insertion.JsonConfig, 'recognizedSpeech', {
								result: result,
								bufferLength: track?.__buffer?.length,
								startOffset: track.startOffset,
								endOffset: track.endOffset
							});
						} else {
							let cuttedBuffer = (track.startOffset || track.endOffset) ? await audio.cut(track.__buffer, track.startOffset, track.__buffer.duration - track.endOffset) : track.__buffer;
							let resampled = cuttedBuffer && await audioHelper.resampleBlob(await audio.bufferToWave(cuttedBuffer), 24000, { mono: true });
							const base64 = await this.$Data.blob2Base64(resampled);
							const result = await this.$Data.speechProcessAudio({
								Language: track.__tracker.language || 'en-US',
								TranslateTo: null,
								ProfileCode: 'SpeechBatchProcessing',
								AllSteps: true
							}, base64).finally(() => this.isProcessing--);

							this.$set(track.insertion.JsonConfig, 'recognizedSpeech', {
								result: result,
								bufferLength: track?.__buffer?.length,
								startOffset: track.startOffset,
								endOffset: track.endOffset
							});
						}
					}
				}

				return track?.insertion?.JsonConfig?.recognizedSpeech?.result;
			}
			Object.defineProperty(track, '__recognizeSpeech', {
				get: () => recognizeSpeech
			})

			let voice = null
			Object.defineProperty(track, '__voice', {
				get: () => {
					if (voice?.id === track?.insertion?.Voice && voice?.locale === track?.insertion?.Language) {
						return voice;
					}
					if (!this.voices) return null;

					voice = this.voices.find(f => f?.id === track?.insertion?.Voice && f?.locale === track?.insertion?.Language);

					return voice;
				}
			});

			_.merge(track, additional);

			if (this.$refs.tracker)
				this.$refs.tracker.initSystemTrackInfo(track);

			linkUnlink.defineLinkUnlinkProperties(track);
			this.defineTrackUndoRedoMethods(track);

			track.__updateLastVersion();
			return track;
		},
	}
};
